import {AnyAction} from "redux";
import {useSelector} from "react-redux";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {Reservation, ReservationStatus} from "crqlar-react-common/models/reservation/reservation.model";
import {endOfDay, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {ReservationGroup} from "crqlar-react-common/dist/models/reservation/reservationGroup.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {AgendaV3} from "crqlar-react-common/dist/models/agenda/agenda.model";
import {FilterRule} from "crqlar-react-common/dist/models/audience/filter.model";
import {ListColumn} from "crqlar-react-common/dist/components/CList/CList";

export enum ReservationActions {
    SET_RESERVATIONS = 'SET_RESERVATIONS'
}

export type ReservationDaySlice = 'all_day' | 'now' | 'morning' | 'day' | 'evening' | 'custom'

export interface ReservationsSectorState {
    selectedResources?: Resource[]
    selectedResourceSingle?: Resource
    selectedServices?: Service[]
    fromDate: Date
    toDate: Date
    searchQuery?: string
    expanded: boolean
    showOnlyWithReservations: boolean
    reservationDaySlice?: ReservationDaySlice,
    reservationDaySliceCustom?: Date,
    tableLayoutFullScreenMode: boolean,
    selectedStatuses: ReservationStatus[]
    activeTab: 'action-center' | 'daily' | 'calendar' | 'floorplan' | 'list' | 'halfboard',
    timelineStep?: number,
    viewMode?: 'timeline' | 'timeGrid'
    timelineSpan?: 'day' | 'week' | 'month',
    blockWholeDay?: boolean,
    resourceCategoryFilter?: string,
    // Mobiscroll resource column -> can represent reservation resource or reservation service
    mbscResourceColumnType?: 'resources' | 'services',
    initialized: boolean,
    checkInDate: Date,
    checkOutDate: Date,
    tmpReservationGroupDialogOpen: boolean
    tmpReservationGroup?: ReservationGroup
    tmpReservationGroupReservations: Reservation[]
    tmpReservationGroupWizardActive: boolean
    tmpReservationGroupWizardReservation?: Reservation
    tmpReservationGroupWizardReservationService?: Service
    startTimeFrame?: string
    endTimeFrame?: string
    selectedAgenda?: AgendaV3
    filterRules?: FilterRule[],
    defaultColumns?: ListColumn[],
    allColumns?: ListColumn[],
    activeColumns?: ListColumn[]
    newReservation?: Reservation
    // Used when forcing rerender of calendar component - when explicit navigation is needed
    calendarNavigationKey?: string
}

const initialSectorState: any = {
    selectedServices: [],
    fromDate: startOfDay(new Date()).toISOString(),
    toDate: endOfDay(new Date()).toISOString(),
    expanded: true,
    showOnlyWithReservations: false,
    reservationDaySlice: undefined,
    reservationDaySliceCustom: undefined,
    selectedStatuses: [],
    tableLayoutFullScreenMode: false,
    activeTab: 'calendar',
    timelineStep: 30,
    blockWholeDay: false,
    initialized: false,
    tmpReservationGroupDialogOpen: false,
    tmpReservationGroupWizardActive: false,
    tmpReservationGroupReservations: [],
    startTimeFrame: '00:00',
    endTimeFrame: '23:59',
    selectedAgenda: undefined,
    filterRules: [],
    defaultColumns: [],
    allColumns: [],
    activeColumns: [],
    newReservation: undefined
}

export interface ReservationsState {
    [appId: string]: ReservationsSectorState
}

const initialState = {}

const validDate = (value: unknown): Date | undefined => {
    const date = value instanceof Date ? new Date(value) : new Date(value as any)
    return Number.isNaN(date.getTime()) ? undefined : date
}

const validDateOr = (value: unknown, fallback: Date): Date => validDate(value) ?? fallback

export const normalizeReservationsSectorState = (
    sectorState?: Partial<ReservationsSectorState>,
): ReservationsSectorState => {
    const mergedState = {
        ...initialSectorState,
        ...sectorState,
    }
    return {
        ...mergedState,
        fromDate: validDateOr(mergedState.fromDate, startOfDay(new Date())),
        toDate: validDateOr(mergedState.toDate, endOfDay(new Date())),
        reservationDaySliceCustom: mergedState.reservationDaySliceCustom
            ? validDate(mergedState.reservationDaySliceCustom)
            : undefined,
    }
}

export const reservationsReducer = (state: ReservationsState = initialState, action: AnyAction): ReservationsState => {
    switch (action.type) {
        case ReservationActions.SET_RESERVATIONS:
            const updatedState = {
                ...state[action.appId],
                ...action.payload
            }

            // Explicitly handle null/undefined for tmpReservationGroup
            if ('tmpReservationGroup' in action.payload) {
                updatedState.tmpReservationGroup = action.payload.tmpReservationGroup
            }

            return {
                ...state,
                [action.appId]: updatedState
            }
        default:
            return state
    }
}

export const useReservationsState = (appId: string): ReservationsSectorState => {
    const sectorState = useSelector<any, ReservationsSectorState>(state => state.reservations[appId])
    return normalizeReservationsSectorState(sectorState)
}
